import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CheckBadgeIcon,
    ExclamationTriangleIcon,
    RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { confirmSubscription } from "interfaces/stripe";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";

const ConfirmPurchase = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const confirmSub = async () => {
            try {
                const result = await confirmSubscription();
                if (result && result.success) {
                    localStorage.setItem("isSub", "true");
                }
            } catch (err: any) {
                console.error("Failed to confirm subscription:", err);
                setError("Impossible de confirmer l'abonnement. Veuillez contacter le support.");
            } finally {
                setLoading(false);
            }
        };

        confirmSub();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-center space-y-4">
                    <div className="mx-auto w-10 h-10 border-4 border-ink border-t-transparent rounded-full motion-safe:animate-spin" />
                    <p className="text-lg text-ink-muted font-medium">Confirmation de votre achat...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-center max-w-md p-8 bg-surface rounded-2xl border border-border">
                    <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-danger-subtle">
                        <ExclamationTriangleIcon className="w-8 h-8 text-danger" />
                    </div>
                    <h2 className="text-2xl font-bold text-ink mb-2">Oups !</h2>
                    <p className="text-ink-muted mb-6">{error}</p>
                    <Button variant="primary" onClick={() => navigate("/pricing")}>
                        Retour aux tarifs
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas font-sans px-4 py-12 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
                {/* Success Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-success rounded-full">
                        <CheckBadgeIcon className="w-10 h-10 text-white" />
                    </div>

                    <h1 className="font-display text-3xl md:text-4xl font-normal tracking-tight leading-tight text-ink mb-3">
                        Confirmation d'Achat
                    </h1>

                    <p className="text-lg text-ink-muted mb-8">
                        Votre paiement a été effectué avec succès
                    </p>

                    <Badge tone="success">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        Compte Premium Activé
                    </Badge>
                </div>

                {/* Main CTA */}
                <div className="bg-surface rounded-2xl border border-border p-6 mb-8">
                    <Button
                        variant="accent"
                        className="w-full"
                        onClick={() => navigate("/dashboard")}
                    >
                        <RocketLaunchIcon className="w-5 h-5" />
                        Accéder à mon Dashboard
                    </Button>
                </div>

                {/* Secondary Links */}
                <div className="text-center text-sm text-ink-muted">
                    Besoin d'aide ?{" "}
                    <a
                        href="mailto:support@duckflow.io"
                        className="text-accent hover:text-accent-hover font-semibold transition-colors duration-150"
                    >
                        Contacter le support
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ConfirmPurchase;
